import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReplenishmentCustomer, ReplenishmentImportResult } from '../models/replenishment';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DownloadFileService } from '../../../services/file-downloader/download-file.service';

@Injectable({
  providedIn: 'root'
})
export class ReplenishmentCustomerService {
  protected replenishmentCustomerSubject: BehaviorSubject<ReplenishmentCustomer[]>;

  constructor(
    protected http: HttpClient,
    protected downloadFileService: DownloadFileService
  ) {}

  private baseUrl = 'json/replenishments/customers';
  private excelBaseUrl = 'replenishment/products/excel';

  public getAllReplenishmentCustomers(): Observable<ReplenishmentCustomer[]> {
    if (!this.replenishmentCustomerSubject) {
      this.updateSubject();
    }

    return this.replenishmentCustomerSubject.asObservable();
  }

  private updateSubject(): void {
    if (!this.replenishmentCustomerSubject) {
      this.replenishmentCustomerSubject = new BehaviorSubject<ReplenishmentCustomer[]>([]);
    }

    this.getAllReplenishmentCustomersInternal().subscribe(rc => {
      this.replenishmentCustomerSubject.next(rc);
    });
  }

  private getAllReplenishmentCustomersInternal(): Observable<ReplenishmentCustomer[]> {
    const headers = {
      maxAge: '-1'
    };
    return this.http.get<ReplenishmentCustomer[]>(this.baseUrl, { headers });
  }

  public getReplenishmentCustomer(id: string): Observable<ReplenishmentCustomer> {
    const url = `${this.baseUrl}/${id}`;
    const headers = {
      maxAge: '-1'
    };
    return this.http.get<ReplenishmentCustomer>(url, { headers });
  }

  saveReplenishmentCustomer(replenishmentCustomer: ReplenishmentCustomer): Observable<ReplenishmentCustomer> {
    const url = `${this.baseUrl}/${replenishmentCustomer.customerId}`;
    return this.http.put<ReplenishmentCustomer>(url, replenishmentCustomer).pipe(tap(_ => this.updateSubject()));
  }

  downloadTemplate(): Observable<any> {
    return this.http.get(`${this.excelBaseUrl}/template`, { responseType: 'arraybuffer', observe: 'response' }).pipe(
      map((response: HttpResponse<ArrayBuffer>) => this.downloadFileService.create(response)),
      map(file => this.downloadFileService.download(file))
    );
  }

  importReplenishmentProducts(formData: FormData): Observable<ReplenishmentImportResult> {
    return this.http.post<ReplenishmentImportResult>(`${this.excelBaseUrl}/import`, formData);
  }
}
